import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader, useFetch, StatusPill, Pagination } from "../../components/crm/ui";
import { Workflow, ArrowRight, Filter, Search, X, ChevronUp, ChevronDown, RefreshCw, UserCheck } from "lucide-react";

const PHASES = ["Due Diligence", "Solutioning", "Regulatory", "Installation", "Subsidy & After-Sales"];

const STATUSES = ["open", "active", "won_verbal", "won", "on_hold", "dropped", "lost"];
const STATUS_TONE = { open: "info", active: "info", won_verbal: "warning", won: "success", lost: "danger", dropped: "muted", on_hold: "warning" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function fmtINR(n) { return `₹${Number(n || 0).toLocaleString("en-IN")}`; }

export default function Opportunities() {
  const { data: opps, reload } = useFetch("/opportunities");
  const { data: customers } = useFetch("/customers");
  const { data: employees } = useFetch("/sales-employees");
  const [scUsers, setScUsers] = useState([]);

  useEffect(() => {
    api.get("/admin/sc").then((r) => setScUsers(r.data || [])).catch(() => {});
  }, []);
  const scMap = useMemo(() => {
    const m = {}; scUsers.forEach((u) => { m[u.id] = { ...u, full_name: u.full_name || u.name }; }); return m;
  }, [scUsers]);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPhase, setFilterPhase] = useState("all");
  const [filterAssigned, setFilterAssigned] = useState("all");
  const [filterSc, setFilterSc] = useState("all");
  const [valueMin, setValueMin] = useState("");
  const [valueMax, setValueMax] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [closeBy, setCloseBy] = useState("");
  const [search, setSearch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Auto-poll every 30s for parity with Leads page
  useEffect(() => {
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function progress(o) {
    if (!o.activities?.length) return 0;
    const done = o.activities.filter((a) => a.status === "completed").length;
    return Math.round((done / o.activities.length) * 100);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const vMin = valueMin ? Number(valueMin) : null;
    const vMax = valueMax ? Number(valueMax) : null;
    const fromTs = createdFrom ? new Date(`${createdFrom}T00:00:00`).getTime() : null;
    const toTs = createdTo ? new Date(`${createdTo}T23:59:59`).getTime() : null;
    const closeByTs = closeBy ? new Date(`${closeBy}T23:59:59`).getTime() : null;

    return opps.filter((o) => {
      if (filterStatus !== "all" && (o.status || "active") !== filterStatus) return false;
      if (filterPhase !== "all" && String(o.current_phase || 1) !== filterPhase) return false;
      if (filterAssigned !== "all") {
        if (filterAssigned === "unassigned" && o.sales_employee_id) return false;
        if (filterAssigned !== "unassigned" && o.sales_employee_id !== filterAssigned) return false;
      }
      if (filterSc === "any" && !o.sc_user_id) return false;
      if (filterSc !== "all" && filterSc !== "any" && o.sc_user_id !== filterSc) return false;

      const v = Number(o.estimated_value || 0);
      if (vMin !== null && v < vMin) return false;
      if (vMax !== null && v > vMax) return false;

      if (fromTs || toTs) {
        const t = o.created_at ? new Date(o.created_at).getTime() : 0;
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
      }
      if (closeByTs && o.expected_close_date) {
        const t = new Date(o.expected_close_date).getTime();
        if (t > closeByTs) return false;
      } else if (closeByTs && !o.expected_close_date) {
        return false;
      }

      if (q) {
        const cust = customers.find((c) => c.id === o.customer_id);
        const hay = [o.code, o.name, cust?.customer_name, cust?.email, cust?.phone, o.notes, scMap[o.sc_user_id]?.full_name].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [opps, filterStatus, filterPhase, filterAssigned, filterSc, valueMin, valueMax, createdFrom, createdTo, closeBy, search, customers, scMap]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "code": av = a.code || ""; bv = b.code || ""; break;
        case "name": av = a.name || ""; bv = b.name || ""; break;
        case "value": av = a.estimated_value || 0; bv = b.estimated_value || 0; break;
        case "phase": av = a.current_phase || 1; bv = b.current_phase || 1; break;
        case "progress": av = progress(a); bv = progress(b); break;
        case "status": av = a.status || "active"; bv = b.status || "active"; break;
        default: av = a.created_at || ""; bv = b.created_at || "";
      }
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  }
  function clearFilters() {
    setFilterStatus("all"); setFilterPhase("all"); setFilterAssigned("all"); setFilterSc("all");
    setValueMin(""); setValueMax(""); setCreatedFrom(""); setCreatedTo(""); setCloseBy(""); setSearch("");
    setPage(1);
  }
  const hasFilters =
    filterStatus !== "all" || filterPhase !== "all" || filterAssigned !== "all" || filterSc !== "all" ||
    valueMin || valueMax || createdFrom || createdTo || closeBy || search.trim();

  useEffect(() => { setPage(1); },
    [filterStatus, filterPhase, filterAssigned, filterSc, valueMin, valueMax, createdFrom, createdTo, closeBy, search, sortKey, sortDir, pageSize]);

  const paged = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return sorted.slice(startIdx, startIdx + pageSize);
  }, [sorted, page, pageSize]);

  function SortHeader({ k, children, className = "" }) {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        data-testid={`opp-sort-${k}`}
        className={"inline-flex items-center gap-1 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] hover:text-[#0F1A12] " + className}
      >
        {children}
        {active && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
    );
  }

  return (
    <div data-testid="opportunities-page">
      <PageHeader overline="Pipeline" title="Opportunities" />

      <div className="bg-white border border-[#DDE5D8] mb-4">
        {/* Filter bar */}
        <div className="px-4 py-3 border-b border-[#DDE5D8] flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#475569]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#475569]">Filters</span>

          <div className="relative ml-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, customer, email…"
              data-testid="opps-search"
              className="pl-8 pr-3 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white w-72"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="opp-filter-status"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>

          <select
            value={filterPhase}
            onChange={(e) => setFilterPhase(e.target.value)}
            data-testid="opp-filter-phase"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All phases</option>
            {PHASES.map((p, i) => <option key={p} value={i + 1}>P{i + 1} · {p}</option>)}
          </select>

          <select
            value={filterAssigned}
            onChange={(e) => setFilterAssigned(e.target.value)}
            data-testid="opp-filter-assigned"
            className="text-xs font-mono border border-[#DDE5D8] px-2.5 py-1.5 bg-white focus:border-[#1F4E2C] outline-none"
          >
            <option value="all">All assignees</option>
            <option value="unassigned">— Unassigned —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>

          {hasFilters && (
            <button
              onClick={clearFilters}
              data-testid="opp-clear-filters"
              className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}

          <button
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="opps-advanced-toggle"
            className="text-[10px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#1F4E2C] inline-flex items-center gap-1"
          >
            {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Advanced
          </button>

          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#475569]">
            <span className="text-[#0F1A12] font-semibold" data-testid="opp-result-count">{sorted.length}</span> / {opps.length}
          </span>

          <button
            onClick={() => reload()}
            data-testid="opps-refresh-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C] transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Advanced filters (collapsible) */}
        {advancedOpen && (
          <div
            className="px-4 py-3 border-b border-[#DDE5D8] bg-[#FAFBF7] grid grid-cols-1 md:grid-cols-4 gap-3"
            data-testid="opps-advanced-row"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Value · ₹ min</div>
              <input type="number" min="0" value={valueMin} onChange={(e) => setValueMin(e.target.value)}
                data-testid="opps-value-min" placeholder="0"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Value · ₹ max</div>
              <input type="number" min="0" value={valueMax} onChange={(e) => setValueMax(e.target.value)}
                data-testid="opps-value-max" placeholder="∞"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Created · from</div>
              <input type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)}
                data-testid="opps-created-from"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Created · to</div>
              <input type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)}
                data-testid="opps-created-to"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Expected close by</div>
              <input type="date" value={closeBy} onChange={(e) => setCloseBy(e.target.value)}
                data-testid="opps-close-by"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white" />
            </div>
            <div className="md:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Solar Consultant</div>
              <select value={filterSc} onChange={(e) => setFilterSc(e.target.value)}
                data-testid="opps-filter-sc"
                className="w-full px-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white">
                <option value="all">All opportunities (SC + Non-SC)</option>
                <option value="any">— Any SC-referred opportunity —</option>
                {scUsers.map((u) => (
                  <option key={u.id} value={u.id}>{(u.full_name || u.name) || "(unnamed)"} · {u.phone || u.email}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="opps-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr>
                <th className="px-3 py-2.5 text-left"><SortHeader k="code">Code</SortHeader></th>
                <th className="px-3 py-2.5 text-left"><SortHeader k="created_at">Created</SortHeader></th>
                <th className="px-3 py-2.5 text-left"><SortHeader k="name">Opportunity</SortHeader></th>
                <th className="px-3 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Customer</th>
                <th className="px-3 py-2.5 text-right"><SortHeader k="value" className="justify-end w-full">Est. value</SortHeader></th>
                <th className="px-3 py-2.5 text-left"><SortHeader k="phase">Phase</SortHeader></th>
                <th className="px-3 py-2.5 text-left"><SortHeader k="progress">Progress</SortHeader></th>
                <th className="px-3 py-2.5 text-left"><SortHeader k="status">Status</SortHeader></th>
                <th className="px-3 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Assigned</th>
                <th className="px-3 py-2.5 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Open</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((o) => {
                const cust = customers.find((c) => c.id === o.customer_id);
                const emp = employees.find((e) => e.id === o.sales_employee_id);
                const sc = o.sc_user_id ? scMap[o.sc_user_id] : null;
                const pct = progress(o);
                const phaseNum = o.current_phase || 1;
                return (
                  <tr key={o.id} data-testid={`opp-row-${o.id}`} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] transition-colors text-[12.5px]">
                    <td className="px-3 py-2 align-top font-mono text-[11px] font-semibold text-[#1F4E2C]" data-testid={`opp-code-${o.id}`}>
                      <Link to={`/crm/opportunities/${o.id}`} className="hover:underline">{o.code || "—"}</Link>
                    </td>
                    <td className="px-3 py-2 align-top font-mono text-[11px] text-[#0F1A12]">{fmtDate(o.created_at)}</td>
                    <td className="px-3 py-2 align-top max-w-[280px]">
                      <Link to={`/crm/opportunities/${o.id}`} className="font-display font-semibold text-[#0F1A12] hover:text-[#1F4E2C] truncate block" title={o.name}>
                        {o.name}
                      </Link>
                      <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5">{o.activities?.length || 0} activities</div>
                      {sc && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-purple-50 text-purple-800 px-1.5 py-0.5 border border-purple-200"
                          data-testid={`opp-sc-${o.id}`}
                          title={`Referred by SC · ${sc.phone || ""}`}
                        >
                          <UserCheck className="w-3 h-3" /> SC: {sc.full_name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-[12.5px] text-[#0F1A12]">{cust?.customer_name || "—"}</div>
                      <div className="font-mono text-[10px] text-[#475569] mt-0.5">{cust?.customer_type?.replace("_", " ")}</div>
                    </td>
                    <td className="px-3 py-2 align-top text-right font-mono text-[11px] text-[#0F1A12]">
                      {fmtINR(o.estimated_value)}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-[10.5px] uppercase tracking-wider text-[#0F1A12]">P{phaseNum}</div>
                      <div className="font-mono text-[9.5px] text-[#475569] mt-0.5">{PHASES[phaseNum - 1]}</div>
                    </td>
                    <td className="px-3 py-2 align-top w-[130px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#EEF2EA] relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 bg-[#1F4E2C] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="font-mono text-[10px] text-[#475569] tabular-nums w-8 text-right">{pct}%</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <StatusPill value={(o.status || "active").replace("_", " ")} tone={STATUS_TONE[o.status || "active"]} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-mono text-[10.5px] text-[#0F1A12]">{emp?.name || <span className="text-[#94A3B8] italic">Unassigned</span>}</div>
                      {emp?.territory && <div className="font-mono text-[9.5px] text-[#94A3B8] mt-0.5">{emp.territory}</div>}
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <Link
                        to={`/crm/opportunities/${o.id}`}
                        data-testid={`opp-open-${o.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-[#1F4E2C] text-white px-2.5 py-1 hover:bg-[#0F1A12] transition-colors"
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Workflow className="w-8 h-8 mx-auto text-[#94A3B8]" />
                    <div className="mt-3 text-sm text-[#475569] font-mono">
                      {opps.length === 0 ? (
                        <>No opportunities yet. Convert a lead from <Link to="/crm/leads" className="underline text-[#1F4E2C]">Leads</Link>.</>
                      ) : "No opportunities match your filters."}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={sorted.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testid="opps-pagination"
        />
      </div>
    </div>
  );
}
